/**
 * Traducción de los filtros del catálogo al evento `catalogo_buscado`.
 *
 * Existe aparte porque las cuatro páginas de catálogo (carros, motos, camiones y
 * vans) son clones del mismo formulario: sin este helper, el mismo mapeo de 14
 * filtros estaría copiado cuatro veces y se desincronizaría al primer filtro nuevo.
 *
 * ⚠️ El filtro por placa NO viaja: la placa identifica un vehículo real y es dato
 * del cliente. Solo se registra que el filtro se usó (la clave `placa` dentro de
 * `filtros`), nunca su valor.
 */
import { limpiarTerminoBusqueda } from "./mixpanel";
import { trackEvent, type AnalyticsEvents, type OrdenCatalogo, type TipoCatalogo } from "./analytics";

/** Normaliza texto de negocio: minúscula y sin espacios sobrantes. */
const normalizar = (valor: string | undefined | null): string | undefined => {
    const v = (valor ?? "").trim().toLowerCase();
    return v || undefined;
};

/** Convierte a número solo si el valor es un número real y positivo. */
const numeroOpcional = (valor: any): number | undefined => {
    const n = Number(valor);
    return Number.isFinite(n) && n > 0 ? n : undefined;
};

/** `true` si el filtro tiene algún valor seleccionado (array, rango o escalar). */
const tieneValor = (valor: any): boolean => {
    if (valor === undefined || valor === null || valor === "") return false;
    if (Array.isArray(valor)) return valor.length > 0;
    if (typeof valor === "object") return numeroOpcional(valor.min) !== undefined || numeroOpcional(valor.max) !== undefined;
    return true;
};

/**
 * Traduce el objeto `orderBy` del formulario al valor estable del catálogo.
 * Los nombres de `orderingOptions` son de UI y pueden cambiar; estos no.
 */
export const ordenDeFormulario = (orderBy: any): OrdenCatalogo => {
    const keyname = orderBy?.value?.keyname;
    if (keyname === "price") return orderBy?.value?.desc ? "precio_desc" : "precio_asc";
    if (keyname === "warranty") return "garantia";
    return "relevancia";
};

/**
 * Claves del formulario → nombre del filtro en la analítica. El orden de esta
 * lista es el orden en que aparecen en `filtros`, así que se mantiene estable
 * para que los reportes no cambien de forma sin querer.
 */
const FILTROS: Array<[string, string]> = [
    ["search", "busqueda"],
    ["brand_id", "marca"],
    ["model", "modelo"],
    ["year", "anio"],
    ["price", "precio"],
    ["type_vehcile_id", "tipo_vehiculo"],
    ["type_transmission", "transmision"],
    ["tag_id", "etiqueta"],
    ["km", "kilometraje"],
    ["type_fuel_id", "combustible"],
    ["color_id", "color"],
    ["plate_number", "placa"],
    ["traccion", "traccion"],
    ["location", "sede"],
];

/** Cuántos filtros tiene aplicados el formulario en este momento. */
export const contarFiltrosActivos = (datos: any): number =>
    FILTROS.filter(([clave]) => tieneValor((datos ?? {})[clave])).length;

/**
 * Nombres de los tipos de vehículo seleccionados, a partir de `type_vehcile_id`.
 *
 * Cada catálogo guarda ese campo con una forma distinta: el de carros usa un
 * arreglo de ids, y los de motos/camiones/vans un escalar o `""` cuando no hay
 * selección. Hacer `.map()` directo sobre el valor crudo revienta en esos tres
 * (`"".map is not a function`), y como la llamada vive dentro del `try` de
 * `_handleSearch`, el `catch` dejaba el listado vacío aunque el backend hubiera
 * respondido bien. Por eso la normalización vive aquí y no en cada página.
 */
export const tiposVehiculoDeFormulario = (valor: any, typeVehicles: { id: any; name: string }[]): string[] => {
    const ids = Array.isArray(valor) ? valor : tieneValor(valor) ? [valor] : [];
    return ids.map((id) => typeVehicles.find((tv) => String(tv.id) === String(id))?.name ?? "").filter(Boolean);
};

export interface ContextoBusqueda {
    tipoCatalogo: TipoCatalogo;
    /** `formFunctions.getValues()` de la página de catálogo. */
    datos: any;
    pagina: number;
    resultados: number;
    /** Nombre de la marca ya resuelto desde el contexto (el formulario guarda el id). */
    marca?: string;
    /** Nombres de los tipos de vehículo ya resueltos desde el contexto. */
    tiposVehiculo?: string[];
}

/** Arma las propiedades de `catalogo_buscado` a partir del estado del formulario. */
export const propsBusquedaCatalogo = (ctx: ContextoBusqueda): AnalyticsEvents["catalogo_buscado"] => {
    const { datos = {} } = ctx;
    const filtros = FILTROS.filter(([clave]) => tieneValor(datos[clave])).map(([, nombre]) => nombre);
    const anios = typeof datos.year === "string" ? datos.year.split("-").filter(Boolean) : undefined;

    return {
        tipo_catalogo: ctx.tipoCatalogo,
        termino_busqueda: limpiarTerminoBusqueda(datos.search),
        resultados_count: ctx.resultados,
        pagina: ctx.pagina,
        filtros_activos: filtros.length,
        filtros,
        orden: ordenDeFormulario(datos.orderBy),
        marca: normalizar(ctx.marca),
        tipos_vehiculo: ctx.tiposVehiculo?.map((t) => normalizar(t)).filter(Boolean) as string[] | undefined,
        precio_min: numeroOpcional(datos.price?.min),
        precio_max: numeroOpcional(datos.price?.max),
        km_max: numeroOpcional(datos.km?.max),
        anios,
        sedes_count: Array.isArray(datos.location) ? datos.location.length || undefined : undefined,
    };
};

/**
 * Última búsqueda emitida por catálogo, para no contarla dos veces.
 *
 * `_handleSearch` se dispara desde varios `useEffect` (filtros, página, rangos
 * con debounce) y en la primera carga se ejecuta más de una vez con los mismos
 * criterios. Sin esta guarda, cada visita al catálogo inflaría el conteo de
 * búsquedas y hundiría artificialmente el CTR hacia las fichas.
 *
 * La firma incluye los resultados: si el backend devuelve algo distinto para los
 * mismos filtros, sí es una búsqueda nueva que vale la pena registrar.
 */
const ultimaFirma = new Map<TipoCatalogo, string>();

/** Emite `catalogo_buscado` descartando repeticiones consecutivas idénticas. */
export const trackCatalogoBuscado = (ctx: ContextoBusqueda): void => {
    // Los catálogos llaman esto dentro del `try` que pinta los resultados: si la
    // analítica lanza, el `catch` de la página borra el listado. Medir nunca puede
    // costar la vista.
    try {
        const props = propsBusquedaCatalogo(ctx);
        const firma = JSON.stringify(props);
        if (ultimaFirma.get(ctx.tipoCatalogo) === firma) return;
        ultimaFirma.set(ctx.tipoCatalogo, firma);
        trackEvent("catalogo_buscado", props);
    } catch (_) {}
};
